import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Link } from '@material-ui/core';
import HTMLEllipsis from 'react-lines-ellipsis/lib/html';

import { ActionButton, Image } from 'components/atoms';
import { CardProduct } from 'components/organisms';
import { Button } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  cardProduct: {
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    boxShadow: 'none',
    '& .card-product__content': {
      paddingTop: 0,
      paddingBottom: 0,
      flex: '1 1 50%',
    },
    '& .card-product__media': {
      width: 356,
      height: 265,
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column !important',
      '& .card-product__content': {
        flex: '1 1 100%',
      },
      '& .card-product__media': {
        flex: '1 1 100%',
        width: '100%',
      },
    },
  },
  cardProductReverse: {
    flexDirection: 'row-reverse',
  },
  image: {
    flex: 'unset',
    objectFit: 'contain',
  },
  blogContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
  button: {
    width: theme.spacing(20),
    marginTop: theme.spacing(2),
    alignSelf: 'flex-start',
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(3),
    },
  },
  showMoreButton: {
    color: 'blue',
    fontFamily: 'Roboto',
    fontWeight: 'normal',
    textAlign: 'left',
    fontSize: 16,
    paddingLeft: 0
  },
  blogTitle: {
    fontSize: 25,
    marginBottom: theme.spacing(1),
    fontWeight: 400,
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  tag: {
    padding: theme.spacing(1 / 2, 1),
    borderRadius: theme.spacing(1 / 2),
    background: theme.palette.primary.main,
    color: 'white',
    margin: theme.spacing(0, 1, 1, 0),
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(0, 2, 2, 0),
    },
  },
  date: {
    color: '#273A42',
    fontSize: '1.12rem',
    fontWeight: '300',
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(2, 0),
    },
  },
  subtitle: {
    fontSize: '1.12rem',
    lineHeight: 'unset',
  },
  description: {
    textAlign: 'justify',
    textJustify: 'inter-word',
  },
}));

const LatestNewsCard = (props) => {
  const {
    item,
    className,
    buttonText,
    trimContent,
    anchorScroll,
    showMoreButton,
    showMoreButtonText,
    showLessButtonText,
  } = props;
  const classes = useStyles();
  const ref = useRef();
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (!anchorScroll) return;

    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [anchorScroll]);

  const NewsMediaContent = (props) => (
    <Image {...props} lazy={false} className={clsx(classes.image)} lazyProps={{ width: '100%', height: '100%' }} />
  );

  const [useEllipsis, setUseEllipsis] = useState(trimContent);

  const handleDescriptionClick = () => {
    if (!trimContent) return;

    setUseEllipsis((prev) => !prev);
  };

  const handleShowMoreButton = () => {
    setShowMore(!showMore);
  };

  const NewsContent = (props) => {
    const subtitleLength = props.subtitle?.length;
    const isShort = subtitleLength < 300;
    const text = isShort ? props.subtitle : (showMore ? props.subtitle : `${props.subtitle.substring(0, 299)}...`);

    return (
      <div className={clsx(classes.blogContent)}>
        {Boolean(props.tags && props.tags.length) && (
          <div className={clsx(classes.tags)}>
            {props.tags.map((item, index) => (
              <Typography variant='caption' className={clsx(classes.tag)} key={index}>
                {item}
              </Typography>
            ))}
          </div>
        )}

        <div>
          <Typography variant='body2' color='textPrimary' className={clsx(classes.date)}>
            {props.date}
          </Typography>

          <Typography variant='h1' component='h1' color='textPrimary' className={clsx(classes.blogTitle)}>
            {props.title}
          </Typography>

          <Typography
            variant='subtitle1'
            color='textPrimary'
            className={classes.subtitle}
            onClick={handleDescriptionClick}
          >
            {useEllipsis ? (
              <HTMLEllipsis
                unsafeHTML={props.subtitle}
                maxLine={trimContent ? 3 : 99}
                ellipsis='...'
                trimRight
                basedOn='letters'
              />
            ) : showMoreButton ? (
              <div>
                <p dangerouslySetInnerHTML={{ __html: text }} className={classes.description}></p>
                {!isShort && (
                  <Button
                    variant='text'
                    size='medium'
                    className={clsx(classes.showMoreButton)}
                    onClick={handleShowMoreButton}
                  >
                    {showMore ? showLessButtonText : showMoreButtonText}
                  </Button>
                )}
              </div>
            ) : (
              <p dangerouslySetInnerHTML={{ __html: props.subtitle }}></p>
            )}
          </Typography>
        </div>
        <ActionButton
          component={Link}
          variant='contained'
          color='primary'
          size='large'
          className={clsx(classes.button)}
          href={props.url}
          target='_blank'
        >
          {buttonText}
        </ActionButton>
      </div>
    );
  };

  return (
    <span ref={ref}>
      <CardProduct
        className={clsx(classes.cardProduct, className)}
        mediaContent={<NewsMediaContent {...item.cover} alt={item.title} />}
        cardContent={
          <NewsContent
            title={item.title}
            subtitle={item.description}
            date={item.date}
            tags={item.tags}
            url={item.url}
          />
        }
      />
    </span>
  );
};

LatestNewsCard.propTypes = {
  /**
   * External classes
   */
  className: PropTypes.string,
  /**
   * Item to be rendered
   */
  item: PropTypes.object.isRequired,
  /**
   * Text to show on button
   */
  buttonText: PropTypes.string,
  /**
   * If true the description text will be
   * trimmed to show only 3 line
   */
  trimContent: PropTypes.bool,
  /**
   * If true page will scroll automatically to element
   */
  anchorScroll: PropTypes.bool,
};

export default LatestNewsCard;
